import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { CONFIRM_MODAL } from '../../../components/modal/ConfirmModal';
import { ACTIVITY_TYPES } from '../../../constants/activity';
import { deleteAsset, showModal } from '../../../redux/actions';
import {
  DELETE_ACTIVITY,
  SUPERSEDE_ACTIVITY,
} from '../../../redux/constants/activity';
import { alertConstants } from '../../../redux/constants';
import {
  useDeleteActivityMutation,
  useSupersedeActivityMutation,
} from '../../../redux/rtk';
import text from '../../../text';

const DELETE_COPY: Record<string, { title: string; body: string }> = {
  [ACTIVITY_TYPES.note]: { title: 'deleteNote', body: 'deleteNoteBody' },
  [ACTIVITY_TYPES.status]: {
    title: 'deleteStatusUpdate',
    body: 'deleteStatusUpdateBody',
  },
};

type Args = {
  activityId: number;
  projectId: number;
  type: string;
  isSuperseded: boolean;
};

export function useJobActivityActions({
  activityId,
  projectId,
  type,
  isSuperseded,
}: Args) {
  const dispatch = useDispatch();
  const [deleteActivityMutation] = useDeleteActivityMutation();
  const [supersedeActivityMutation] = useSupersedeActivityMutation();

  const dispatchError = useCallback(
    (error: unknown) => {
      dispatch({
        type: alertConstants.ALERT_ERROR,
        payload: {
          message:
            error instanceof AxiosError
              ? error.response?.data?.message
              : 'Unknown error',
        },
      });
    },
    [dispatch]
  );

  const onDeleteActivity = useCallback(() => {
    dispatch(
      showModal(CONFIRM_MODAL, {
        onConfirm: async () => {
          try {
            await deleteActivityMutation({
              projectId,
              activityId,
            }).unwrap();
            dispatch({
              type: DELETE_ACTIVITY,
              payload: { projectId, activityId },
            });
          } catch (error) {
            dispatchError(error);
          }
        },
        title: text(DELETE_COPY[type]?.title ?? 'deleteFileUploadActivity'),
        body: text(DELETE_COPY[type]?.body ?? 'deleteFileUploadActivityBody'),
        okLabel: text('delete'),
      })
    );
  }, [
    activityId,
    projectId,
    type,
    dispatch,
    deleteActivityMutation,
    dispatchError,
  ]);

  const onDeleteAsset = useCallback(
    (assetId: string) => {
      dispatch(
        showModal(CONFIRM_MODAL, {
          onConfirm: () => dispatch(deleteAsset(activityId, assetId)),
          title: text('deleteFile'),
          body: text('deleteFileBody'),
          okLabel: text('delete'),
        })
      );
    },
    [activityId, dispatch]
  );

  const onSupersede = useCallback(() => {
    dispatch(
      showModal(CONFIRM_MODAL, {
        onConfirm: async () => {
          try {
            const result = await supersedeActivityMutation({
              projectId,
              activityId,
              isSuperseded,
            }).unwrap();
            // TODO: redundant — the mutation already invalidates the Activity tag.
            dispatch({
              type: SUPERSEDE_ACTIVITY,
              payload: {
                projectId,
                activityId,
                supersededAssets: result,
              },
            });
          } catch (error) {
            dispatchError(error);
          }
        },
        title: text('supersedeConfirmTitle', {
          action: isSuperseded ? 'reverse supersede' : 'supersede',
        }),
        body: text('supersedeConfirmBody', {
          action: isSuperseded ? 'Reversed superseded' : 'Superseded',
          include: isSuperseded ? 'be' : 'not be',
        }),
        okLabel: text('supersedeConfirmYes', {
          action: isSuperseded ? 'reverse supersede' : 'supersede',
        }),
        cancelLabel: text('supersedeConfirmNo'),
        isLinkButton: true,
        isButtonFullWidth: false,
        isFooterButtonReversed: true,
        isAttentionIconVisible: true,
        isCloseButtonVisible: true,
      })
    );
  }, [
    activityId,
    projectId,
    isSuperseded,
    dispatch,
    supersedeActivityMutation,
    dispatchError,
  ]);

  return { onDeleteActivity, onDeleteAsset, onSupersede };
}
